import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import { getAccessToken, buildQuery, unwrapList, getTransportError,getActiveCompanyId } from './apiUtils';

export interface BankAccountRecord {
  id: string;
  name: string;
  balance: number;
  currency: string;
  status: string;
}

export interface BankMovement {
  id: string;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  date: string;
}

export const mapBankAccount = (raw: any): BankAccountRecord => ({
  id:       String(raw.id),
  name:     raw.name || 'Cuenta Bancaria',
  balance:  parseFloat(raw.balance) || 0,
  currency: raw.currency || 'EUR',
  status:   raw.status || 'active'
});

export const mapBankMovement = (raw: any): BankMovement => {
  const amount = Math.abs(parseFloat(raw.amount) || 0);
  const type = raw.movement_type === 'expense' ? 'expense' : 'income';
  return {
    id:          String(raw.id),
    description: raw.description || 'Movimiento bancario',
    amount:      type === 'expense' ? -amount : amount,
    type,
    date:        raw.movement_date || ''
  };
};

export const listBankAccounts = async (): Promise<BankAccountRecord[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(`/bank_accounts_list${buildQuery({ access_token: token, company_id: getActiveCompanyId() })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  return unwrapList(res).map(mapBankAccount);
};

export const listBankMovements = async (bankAccountId: string): Promise<BankMovement[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const res = await ObexRequestsHandlerP80.get(
    `/bank_movements_list${buildQuery({ access_token: token, bank_account_id: bankAccountId })}`
  );
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  return unwrapList(res).map(mapBankMovement);
};

// Movimiento con su estado de conciliación
export interface ReconcilableMovement {
  id: string;
  bankAccountId: string;
  description: string;
  amount: number;
  date: string;
  reconciled: boolean;
  invoiceId: string;
}

const mapReconcilableMovement = (raw: any): ReconcilableMovement => {
  const amount = Math.abs(parseFloat(raw.amount) || 0);
  return {
    id:            String(raw.id),
    bankAccountId: raw.bank_account_id != null ? String(raw.bank_account_id) : '',
    description:   raw.description || '',
    amount:        raw.movement_type === 'expense' ? -amount : amount,
    date:          raw.movement_date ? String(raw.movement_date).slice(0, 10) : '',
    reconciled:    raw.reconciled === true || raw.reconciled === 'true' || raw.reconciled === 1,
    invoiceId:     raw.reconciled_invoice_id != null ? String(raw.reconciled_invoice_id) : ''
  };
};

// Sin bank_account_id devuelve los de todas las cuentas
export const listReconcilableMovements = async (opts: { bankAccountId?: string; reconciled?: boolean } = {}): Promise<ReconcilableMovement[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`/bank_movements_list${buildQuery({
    access_token:    token,
    bank_account_id: opts.bankAccountId,
    reconciled:      opts.reconciled === undefined ? undefined : String(opts.reconciled)
  })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  return unwrapList(res).map(mapReconcilableMovement);
};

/** El backend rechaza (409) un movimiento que ya existe en la cuenta. */
export class DuplicateBankMovementError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DuplicateBankMovementError';
    Object.setPrototypeOf(this, DuplicateBankMovementError.prototype);
  }
}

// El backend no avisa si el INSERT falla: se comprueba que devuelve este movimiento
export const importBankMovement = async (bankAccountId: string, mv: { description: string; amount: number; date: string }): Promise<string> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  // Sin comillas: el backend las mete tal cual en el SQL
  const description = mv.description.replace(/'/g, '').slice(0, 250);
  const res = await ObexRequestsHandlerP80.get(`/bank_movements_import${buildQuery({
    access_token:    token,
    bank_account_id: bankAccountId,
    description,
    amount:          Math.abs(mv.amount).toFixed(2),
    movement_type:   mv.amount < 0 ? 'expense' : 'income',
    movement_date:   mv.date
  })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  let out = res;
  if (out && out.data) out = out.data;
  if (out && out.result) out = out.result;
  const record = out ? out.response : null;
  if (typeof record === 'string' && /duplicad/i.test(record)) throw new DuplicateBankMovementError(record);
  if (!record || typeof record !== 'object' || !record.id || String(record.description || '') !== description) {
    throw new Error(typeof record === 'string' ? record : 'El banco no confirmó el movimiento.');
  }
  return String(record.id);
};

export const reconcileBankMovement = async (movementId: string, invoiceId = '') => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');
  const res = await ObexRequestsHandlerP80.get(`/bank_movements_reconcile${buildQuery({
    access_token:          token,
    id:                    movementId,
    reconciled_invoice_id: invoiceId
  })}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(transportError);
  let out = res;
  if (out && out.data) out = out.data;
  if (out && out.result) out = out.result;
  const payload = out ? out.response : null;
  if (!payload || typeof payload !== 'object' || payload.reconciled !== true) {
    throw new Error(typeof payload === 'string' ? payload : 'El movimiento no quedó marcado como conciliado.');
  }
};
